import { motion, AnimatePresence, Variants } from 'framer-motion';
import {
  Award, BookOpen, BookOpenCheck, Briefcase, Calendar, CheckCircle,
  Clock, Code2, Database, GitBranch, Globe, GraduationCap, Mail,
  MapPin, MessageCircle, Monitor, Palette, Phone, Rocket, Server,
  Star, Target, TrendingUp, Trophy, UserCheck, Users, Zap, Shield, Sparkles
} from 'lucide-react';
import React, { useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ─── Brand Tokens ─────────────────────────────────────────────────────────────
const C = {
  primary:   '#2b2240',
  secondary: '#4e3a69',
  accent:    '#F0B429',
  orange:    '#FF6535',
  dark:      '#0d0b14',
  muted:     '#8b7aa8',
};

// ─── Keyframes ────────────────────────────────────────────────────────────────
const scrollX = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;
const floatY = keyframes`
  0%,100% { transform: translateY(0px) rotate(0deg); }
  33%      { transform: translateY(-24px) rotate(4deg); }
  66%      { transform: translateY(-12px) rotate(-3deg); }
`;
const pulseGlow = keyframes`
  0%,100% { box-shadow: 0 0 20px rgba(240,180,41,0.25); }
  50%      { box-shadow: 0 0 50px rgba(240,180,41,0.55), 0 0 90px rgba(240,180,41,0.15); }
`;
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const shimmerSlide = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Page Shell ───────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${C.dark};
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  overflow-x: hidden;
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────
const HeroWrap = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 6rem 2rem 9rem;
  overflow: hidden;
  @media (max-width: 768px) { padding: 5rem 1.25rem 7rem; min-height: auto; }
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 80% at 15% 15%, rgba(78,58,105,0.7) 0%, transparent 60%),
    radial-gradient(ellipse 60% 60% at 85% 85%, rgba(255,101,53,0.18) 0%, transparent 55%),
    radial-gradient(ellipse 50% 50% at 50% 0%,  rgba(240,180,41,0.1)  0%, transparent 50%),
    ${C.dark};
`;

const Orb = styled.div<{ $size: number; $top: string; $left: string; $delay: number; $color: string }>`
  position: absolute;
  width:  ${p => p.$size}px;
  height: ${p => p.$size}px;
  top:  ${p => p.$top};
  left: ${p => p.$left};
  background: radial-gradient(circle, ${p => p.$color} 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.35;
  animation: ${floatY} ${p => 7 + p.$delay * 1.5}s ease-in-out infinite;
  animation-delay: ${p => p.$delay}s;
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 860px;
  width: 100%;
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(240,180,41,0.12);
  border: 1px solid rgba(240,180,41,0.35);
  color: ${C.accent};
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 1.75rem;
  backdrop-filter: blur(10px);
  svg { width: 14px; height: 14px; }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.4rem, 5.5vw, 4.8rem);
  font-weight: 900;
  line-height: 1.08;
  color: white;
  margin-bottom: 1.5rem;
  letter-spacing: -0.5px;
  .g {
    background: linear-gradient(135deg, ${C.accent} 0%, ${C.orange} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const HeroSub = styled(motion.p)`
  font-size: clamp(1rem, 2.2vw, 1.25rem);
  color: rgba(255,255,255,0.6);
  max-width: 560px;
  margin: 0 auto 3rem;
  line-height: 1.75;
`;

const HeroStats = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  @media (max-width: 480px) { gap: 0.875rem; }
`;

const StatPill = styled(motion.div)`
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 18px;
  padding: 1.25rem 1.75rem;
  min-width: 140px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: default;
  &:hover {
    background: rgba(240,180,41,0.1);
    border-color: rgba(240,180,41,0.3);
    transform: translateY(-4px);
  }
  .num {
    display: block;
    font-size: 2.25rem;
    font-weight: 900;
    background: linear-gradient(135deg, ${C.accent}, ${C.orange});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
    margin-bottom: 0.4rem;
  }
  .lbl {
    font-size: 0.78rem;
    color: rgba(255,255,255,0.55);
    font-weight: 600;
    letter-spacing: 0.4px;
  }
  @media (max-width: 480px) {
    min-width: 110px;
    padding: 1rem 1.25rem;
    .num { font-size: 1.8rem; }
  }
`;

// ─── Content Card ─────────────────────────────────────────────────────────────
const ContentWrap = styled.div`
  background: #f5f2ff;
  border-radius: 40px 40px 0 0;
  margin-top: -60px;
  position: relative;
  z-index: 10;
  overflow: hidden;
  box-shadow: 0 -30px 80px rgba(0,0,0,0.45);
`;

const Section = styled.section<{ $alt?: boolean }>`
  padding: 5rem 2rem;
  background: ${p => p.$alt ? 'white' : '#f5f2ff'};
  @media (max-width: 768px) { padding: 3rem 1.25rem; }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

// ─── Section Header helpers ────────────────────────────────────────────────────
const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(240,180,41,0.12);
  border: 1px solid rgba(240,180,41,0.25);
  color: ${C.accent};
  padding: 0.3rem 1rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.875rem;
  svg { width: 12px; height: 12px; }
`;

const Heading = styled.h2<{ $center?: boolean }>`
  font-size: clamp(1.75rem, 3vw, 2.6rem);
  font-weight: 900;
  color: ${C.primary};
  text-align: ${p => p.$center ? 'center' : 'left'};
  margin-bottom: 0.875rem;
  line-height: 1.2;
  .a { color: ${C.accent}; }
  .o { color: ${C.orange}; }
`;

const Sub = styled.p<{ $center?: boolean }>`
  color: #777;
  font-size: 1rem;
  line-height: 1.75;
  text-align: ${p => p.$center ? 'center' : 'left'};
  max-width: 560px;
  ${p => p.$center && 'margin: 0 auto;'}
  margin-bottom: 2.5rem;
`;

const Bar = styled.div`
  width: 48px;
  height: 4px;
  background: linear-gradient(90deg, ${C.accent}, ${C.orange});
  border-radius: 2px;
  margin-bottom: 1.25rem;
`;

// ─── Form ─────────────────────────────────────────────────────────────────────
const FormCard = styled(motion.div)`
  background: white;
  border-radius: 28px;
  box-shadow: 0 30px 80px rgba(43,34,64,0.14), 0 0 0 1px rgba(240,180,41,0.12);
  overflow: hidden;
  max-width: 800px;
  margin: 0 auto;
`;

const FormHead = styled.div`
  background: linear-gradient(135deg, ${C.primary} 0%, ${C.secondary} 100%);
  padding: 2.5rem 2.5rem 2.25rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -15%;
    width: 280px;
    height: 280px;
    background: radial-gradient(circle, rgba(240,180,41,0.18) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(255,101,53,0.12) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  h3 {
    color: white;
    font-size: 1.75rem;
    font-weight: 900;
    margin-bottom: 0.5rem;
    position: relative;
    z-index: 1;
    span {
      background: linear-gradient(135deg, ${C.accent}, ${C.orange});
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
  p {
    color: rgba(255,255,255,0.65);
    font-size: 0.9rem;
    position: relative;
    z-index: 1;
  }
  @media (max-width: 768px) {
    padding: 2rem 1.5rem 1.75rem;
    h3 { font-size: 1.35rem; }
  }
`;

const FormBody = styled.div`
  padding: 2.5rem;
  @media (max-width: 768px) { padding: 1.5rem; }
`;

const OfferBanner = styled.div`
  background: linear-gradient(135deg, ${C.primary}, ${C.secondary});
  background-size: 200% 200%;
  animation: ${gradientShift} 5s ease infinite;
  border-radius: 14px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  border: 1px solid rgba(240,180,41,0.3);
  animation: ${pulseGlow} 3s ease-in-out infinite;
  svg { color: ${C.accent}; width: 18px; height: 18px; flex-shrink: 0; }
  .hl { color: ${C.accent}; }
  @media (max-width: 600px) { flex-wrap: wrap; text-align: center; gap: 0.5rem; font-size: 0.85rem; }
`;

const MiniStatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const MiniStat = styled.div`
  background: linear-gradient(135deg, ${C.primary}07, ${C.secondary}12);
  border: 1.5px solid ${C.primary}14;
  border-radius: 14px;
  padding: 1.1rem;
  text-align: center;
  transition: all 0.3s ease;
  &:hover { border-color: ${C.accent}50; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(240,180,41,0.1); }
  .v {
    font-size: 1.7rem;
    font-weight: 900;
    color: ${C.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin-bottom: 0.25rem;
    svg { color: ${C.accent}; width: 18px; height: 18px; }
  }
  .k { font-size: 0.8rem; color: #999; font-weight: 600; }
`;

const WhyBox = styled.div`
  background: linear-gradient(135deg, ${C.primary}07, ${C.accent}08);
  border: 1.5px solid ${C.accent}22;
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
  .title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    color: ${C.primary};
    font-size: 0.88rem;
    margin-bottom: 0.75rem;
    svg { color: ${C.accent}; width: 16px; height: 16px; }
  }
  .chips { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .chip {
    background: ${C.accent}15;
    border: 1px solid ${C.accent}28;
    color: ${C.primary};
    padding: 0.25rem 0.75rem;
    border-radius: 50px;
    font-size: 0.78rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    svg { color: ${C.accent}; width: 11px; height: 11px; }
  }
`;

const FSTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: ${C.primary};
  font-size: 1rem;
  font-weight: 800;
  margin-top: 2rem;
  margin-bottom: 1.25rem;
  padding-bottom: 0.625rem;
  border-bottom: 2px solid ${C.primary}10;
  .icon-wrap {
    width: 30px;
    height: 30px;
    background: ${C.accent}18;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    svg { color: ${C.accent}; width: 16px; height: 16px; }
  }
  @media (max-width: 768px) { font-size: 0.92rem; margin-top: 1.5rem; }
`;

const FieldGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: ${p => p.$cols === 2 ? '1fr 1fr' : '1fr'};
  gap: 1.1rem;
  @media (max-width: 580px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const FLabel = styled.label`
  font-size: 0.82rem;
  font-weight: 800;
  color: ${C.primary};
  display: flex;
  align-items: center;
  gap: 0.35rem;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  svg { color: ${C.accent}; width: 13px; height: 13px; }
  .req { color: ${C.orange}; }
`;

const inputBase = css`
  width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 1rem;
  border: 2px solid #ede8f7;
  border-radius: 11px;
  font-size: 0.95rem;
  font-family: inherit;
  color: ${C.primary};
  background: white;
  transition: all 0.22s ease;
  outline: none;
  &::placeholder { color: #c5bcd8; }
  &:hover { border-color: #c5b5e0; }
  &:focus {
    border-color: ${C.accent};
    box-shadow: 0 0 0 4px rgba(240,180,41,0.1);
    background: #fffdf5;
  }
  @media (max-width: 768px) { font-size: 16px; }
`;

const SInput = styled.input`${inputBase}`;
const SSelect = styled.select`
  ${inputBase}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232b2240' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
`;

const RadioCards = styled.div`
  display: flex;
  gap: 0.875rem;
  flex-wrap: wrap;
`;

const RCard = styled.div<{ $on: boolean }>`
  flex: 1;
  min-width: 90px;
  padding: 0.75rem 1rem;
  border: 2px solid ${p => p.$on ? C.accent : '#ede8f7'};
  background: ${p => p.$on ? `rgba(240,180,41,0.08)` : 'white'};
  border-radius: 11px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 700;
  font-size: 0.9rem;
  color: ${p => p.$on ? C.primary : '#aaa'};
  user-select: none;
  &:hover { border-color: ${C.accent}; color: ${C.primary}; }
  .dot {
    width: 17px;
    height: 17px;
    border-radius: 50%;
    border: 2px solid ${p => p.$on ? C.accent : '#ccc'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.2s;
    &::after {
      content: '';
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${C.accent};
      display: ${p => p.$on ? 'block' : 'none'};
    }
  }
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  @media (max-width: 580px) { grid-template-columns: 1fr; }
`;

const TCard = styled.div<{ $on: boolean; $off: boolean }>`
  border: 2px solid ${p => p.$on ? C.accent : '#ede8f7'};
  background: ${p => p.$on
    ? 'linear-gradient(135deg, rgba(240,180,41,0.1), rgba(255,101,53,0.05))'
    : p.$off ? '#fafafa' : 'white'};
  border-radius: 14px;
  padding: 1.25rem 1rem;
  text-align: center;
  cursor: ${p => p.$off ? 'not-allowed' : 'pointer'};
  transition: all 0.25s ease;
  opacity: ${p => p.$off ? 0.55 : 1};
  ${p => p.$on && css`animation: ${pulseGlow} 3s ease-in-out infinite;`}
  ${p => !p.$off && !p.$on && css`&:hover { border-color: ${C.accent}; transform: translateY(-3px); box-shadow: 0 8px 22px rgba(240,180,41,0.15); }`}
  .si {
    width: 38px;
    height: 38px;
    background: ${p => p.$on ? `linear-gradient(135deg, ${C.accent}, ${C.orange})` : '#f0ebff'};
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.7rem;
    transition: background 0.25s;
    svg { width: 18px; height: 18px; color: ${p => p.$on ? 'white' : C.secondary}; }
  }
  .st { font-weight: 800; color: ${C.primary}; font-size: 0.88rem; margin-bottom: 0.2rem; }
  .sl { font-size: 0.75rem; color: #aaa; margin-bottom: 0.5rem; }
  .ss { font-size: 0.72rem; font-weight: 700; color: ${p => p.$off ? '#ef4444' : p.$on ? C.accent : '#22c55e'}; }
`;

const NoteBox = styled.div<{ $variant?: 'info' | 'warn' }>`
  background: ${p => p.$variant === 'warn' ? 'rgba(255,101,53,0.06)' : 'rgba(43,34,64,0.04)'};
  border: 1.5px solid ${p => p.$variant === 'warn' ? 'rgba(255,101,53,0.2)' : 'rgba(43,34,64,0.1)'};
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  margin-top: 1rem;
  .nt {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 0.85rem;
    color: ${p => p.$variant === 'warn' ? C.orange : C.primary};
    margin-bottom: 0.6rem;
    svg { width: 16px; height: 16px; flex-shrink: 0; }
  }
  ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.4rem; }
  li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: #666;
    svg { color: #22c55e; width: 14px; height: 14px; flex-shrink: 0; }
  }
`;

const VError = styled(motion.div)`
  background: #fff1f1;
  color: #c0392b;
  border: 1.5px solid #fecaca;
  border-radius: 11px;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  svg { width: 16px; height: 16px; flex-shrink: 0; color: #c0392b; }
`;

const SubmitBtn = styled(motion.button)`
  width: 100%;
  padding: 1.05rem 2rem;
  background: linear-gradient(135deg, ${C.orange} 0%, ${C.accent} 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  box-shadow: 0 10px 30px rgba(255,101,53,0.38);
  margin-top: 1.75rem;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
    transition: left 0.5s ease;
  }
  &:hover:not(:disabled)::before { left: 100%; }
  &:hover:not(:disabled) { box-shadow: 0 15px 40px rgba(255,101,53,0.5); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  svg { width: 20px; height: 20px; }
`;

const TrustRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  background: ${C.primary}05;
  border-radius: 12px;
  .t {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.78rem;
    color: #888;
    font-weight: 700;
    svg { color: #22c55e; width: 15px; height: 15px; }
  }
`;

const SuccessBox = styled(motion.div)`
  background: linear-gradient(135deg, #f0fff4, #e6ffed);
  border: 2px solid #86efac;
  border-radius: 18px;
  padding: 2rem;
  text-align: center;
  margin-top: 1.5rem;
  .ic {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #22c55e, #4ade80);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    svg { color: white; width: 28px; height: 28px; }
  }
  h4 { color: #166534; font-size: 1.1rem; font-weight: 800; margin-bottom: 0.4rem; }
  p  { color: #166534; font-size: 0.875rem; opacity: 0.85; }
`;

const ErrorBox = styled(motion.div)`
  background: #fff1f1;
  border: 2px solid #fca5a5;
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  margin-top: 1.5rem;
  color: #7f1d1d;
  font-weight: 700;
  font-size: 0.9rem;
`;

// ─── Feature Cards ─────────────────────────────────────────────────────────────
const FGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 1.5rem;
`;

const FCard = styled(motion.div)`
  background: white;
  border: 1.5px solid ${C.primary}10;
  border-radius: 20px;
  padding: 2rem 1.75rem;
  position: relative;
  overflow: hidden;
  cursor: default;
  transition: all 0.35s ease;
  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${C.accent}, ${C.orange});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }
  &:hover {
    transform: translateY(-7px);
    box-shadow: 0 22px 55px rgba(43,34,64,0.13);
    border-color: ${C.accent}35;
    &::after { transform: scaleX(1); }
    .fi { background: linear-gradient(135deg, ${C.accent}, ${C.orange}); svg { color: white; } }
  }
`;

const FIcon = styled.div`
  width: 52px;
  height: 52px;
  background: ${C.primary}0e;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  transition: all 0.3s ease;
  svg { width: 24px; height: 24px; color: ${C.secondary}; transition: color 0.3s; }
`;

// ─── Career Tracks ─────────────────────────────────────────────────────────────
const TGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 1.5rem;
`;

const TCard2 = styled(motion.div)<{ $c: string }>`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1.5px solid ${p => p.$c}22;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-7px);
    box-shadow: 0 22px 55px ${p => p.$c}28;
    border-color: ${p => p.$c}55;
  }
`;

const THead = styled.div<{ $bg: string }>`
  background: ${p => p.$bg};
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  svg { color: white; width: 20px; height: 20px; }
  h3 { color: white; font-size: 0.95rem; font-weight: 800; margin: 0; }
`;

const TBody = styled.div`
  padding: 1.25rem 1.5rem;
  ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.55rem; }
  li {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    font-size: 0.85rem;
    color: #666;
    line-height: 1.5;
    svg { color: #22c55e; width: 13px; height: 13px; flex-shrink: 0; margin-top: 2px; }
  }
`;

// ─── Tools ─────────────────────────────────────────────────────────────────────
const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 1.1rem;
  max-width: 880px;
  margin: 0 auto;
`;

const ToolCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1.4rem 0.875rem;
  text-align: center;
  border: 1.5px solid transparent;
  box-shadow: 0 3px 14px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  cursor: default;
  &:hover {
    transform: translateY(-6px) scale(1.04);
    .tib { transform: scale(1.12); }
  }
`;

const TIB = styled.div<{ $bg: string; $shadow: string }>`
  width: 52px;
  height: 52px;
  background: ${p => p.$bg};
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.8rem;
  box-shadow: 0 6px 18px ${p => p.$shadow};
  transition: transform 0.3s ease;
  svg { width: 26px; height: 26px; }
`;

const TName = styled.p`
  font-weight: 800;
  font-size: 0.77rem;
  color: ${C.primary};
  margin: 0;
  letter-spacing: 0.2px;
`;

// ─── Testimonials ──────────────────────────────────────────────────────────────
const TestiWrap = styled.div`
  overflow: hidden;
  position: relative;
  padding: 0.5rem 0;
  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 120px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  &::before { left: 0;  background: linear-gradient(to right, white, transparent); }
  &::after  { right: 0; background: linear-gradient(to left,  white, transparent); }
`;

const TestiTrack = styled.div`
  display: flex;
  gap: 1.5rem;
  animation: ${scrollX} 42s linear infinite;
  width: max-content;
  &:hover { animation-play-state: paused; }
`;

const TestiCard = styled.div`
  width: 310px;
  flex-shrink: 0;
  background: white;
  border: 1.5px solid ${C.primary}0e;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 6px 22px rgba(43,34,64,0.07);
  transition: all 0.3s ease;
  position: relative;
  &::before {
    content: '"';
    position: absolute;
    top: 0.875rem;
    right: 1.25rem;
    font-size: 4.5rem;
    line-height: 1;
    font-family: Georgia, serif;
    color: ${C.accent}18;
    font-weight: bold;
    pointer-events: none;
  }
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(43,34,64,0.14);
    border-color: ${C.accent}30;
  }
`;

const TAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${C.primary}, ${C.secondary});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 900;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  border: 3px solid ${C.accent}35;
`;

// ─── Journey ───────────────────────────────────────────────────────────────────
const JGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  position: relative;
  margin-top: 2rem;
  &::before {
    content: '';
    position: absolute;
    top: 50px;
    left: 12.5%;
    right: 12.5%;
    height: 2px;
    background: linear-gradient(90deg, ${C.accent}, ${C.orange}, ${C.accent});
    z-index: 0;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    &::before { display: none; }
  }
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const JStep = styled(motion.div)`
  text-align: center;
  padding: 0 1.25rem 1.5rem;
  position: relative;
  z-index: 1;
`;

const JBubble = styled.div`
  width: 62px;
  height: 62px;
  background: linear-gradient(135deg, ${C.primary}, ${C.secondary});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  border: 4px solid white;
  box-shadow: 0 8px 25px rgba(43,34,64,0.3);
  position: relative;
  transition: all 0.3s ease;
  svg { color: white; width: 26px; height: 26px; }
  &:hover { transform: scale(1.08); box-shadow: 0 12px 30px rgba(43,34,64,0.4), 0 0 0 4px rgba(240,180,41,0.2); }
`;

const JNum = styled.div`
  position: absolute;
  top: -8px;
  right: -4px;
  background: linear-gradient(135deg, ${C.accent}, ${C.orange});
  color: ${C.primary};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 900;
  border: 2px solid white;
`;

// ─── Types ────────────────────────────────────────────────────────────────────
interface FormData {
  name: string; phone: string; email: string;
  yearOfPassout?: string; degree?: string;
  collegeName: string; location?: string;
  completedCourse?: string; selectedCourse?: string;
  previousCourseName?: string; previousCourseDuration?: string;
  consultationSlot?: string;
}
interface TimeSlot { id: string; date: string; time: string; datetime: string; available: boolean; }

// ─── Component ────────────────────────────────────────────────────────────────
const PayAfterForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', collegeName: '',
    degree: '', yearOfPassout: '', location: '',
    completedCourse: '', selectedCourse: '',
    previousCourseName: '', previousCourseDuration: '', consultationSlot: '',
  });

  const [isSubmitting,    setIsSubmitting]    = useState(false);
  const [submitStatus,    setSubmitStatus]    = useState<'idle'|'success'|'error'>('idle');
  const [errorMessage,    setErrorMessage]    = useState('');
  const [validationError, setValidationError] = useState('');
  const formRef = useRef(null);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    if (validationError) setValidationError('');
  };
  const handleRadio = (name: string, value: string) => {
    setFormData(p => ({ ...p, [name]: value }));
    if (validationError) setValidationError('');
  };

  const validateForm = (d: FormData): boolean => {
    setValidationError('');
    if (!d.name.trim())        { setValidationError('Please enter your full name'); return false; }
    if (!d.phone.trim())       { setValidationError('Please enter your phone number'); return false; }
    if (!d.email.trim())       { setValidationError('Please enter your email address'); return false; }
    if (!d.collegeName.trim()) { setValidationError('Please enter your college name'); return false; }
    if (!d.location?.trim())   { setValidationError('Please enter your current location'); return false; }
    if (!d.completedCourse)    { setValidationError('Please select whether you have completed any programming course before'); return false; }
    if (d.completedCourse === 'yes') {
      if (!d.previousCourseName?.trim())    { setValidationError('Please enter the name of the course you completed'); return false; }
      if (!d.previousCourseDuration?.trim()) { setValidationError('Please select the duration of your previous course'); return false; }
    }
    if (!d.selectedCourse?.trim())   { setValidationError('Please select which course you are interested in'); return false; }
    if (!d.consultationSlot?.trim()) { setValidationError('Please select your preferred time slot for technical consultation'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) { setValidationError('Please enter a valid email address'); return false; }
    if (!/^\d{10}$/.test(d.phone))   { setValidationError('Phone number must be exactly 10 digits'); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    setValidationError('');
    const URL = 'https://script.google.com/macros/s/AKfycbzy-RGvx5jxSFWoI-DF7aBabM5paeZ7x79eiDLUuu48Skg_wEtzf5yxgHsACXxClmoQ/exec';
    try {
      await fetch(URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, submissionDate: new Date().toISOString(), userLogin: 'vistatechvt' }),
        mode: 'no-cors',
      });
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name:'',phone:'',email:'',collegeName:'',degree:'',yearOfPassout:'',location:'',completedCourse:'',selectedCourse:'',previousCourseName:'',previousCourseDuration:'',consultationSlot:'' });
      if (formRef.current) (formRef.current as HTMLFormElement).reset();
    } catch (err) {
      setIsSubmitting(false);
      setSubmitStatus('error');
      setErrorMessage('Error: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  const tools = [
    { name: 'Git',        icon: GitBranch, bg: 'linear-gradient(135deg,#f05032,#ff6b4a)', shadow: 'rgba(240,80,50,0.35)',   color: 'white' },
    { name: 'JavaScript', icon: Code2,     bg: 'linear-gradient(135deg,#f7df1e,#ffd700)', shadow: 'rgba(247,223,30,0.35)',  color: '#000'  },
    { name: 'MongoDB',    icon: Database,  bg: 'linear-gradient(135deg,#47a248,#4db33d)', shadow: 'rgba(71,162,72,0.35)',   color: 'white' },
    { name: 'Node.js',    icon: Server,    bg: 'linear-gradient(135deg,#339933,#68cc68)', shadow: 'rgba(51,153,51,0.35)',   color: 'white' },
    { name: 'React',      icon: Code2,     bg: 'linear-gradient(135deg,#61dafb,#21d4fd)', shadow: 'rgba(97,218,251,0.35)',  color: '#000'  },
    { name: 'Redux',      icon: Zap,       bg: 'linear-gradient(135deg,#764abc,#9c68d1)', shadow: 'rgba(118,74,188,0.35)',  color: 'white' },
    { name: 'Express',    icon: Server,    bg: 'linear-gradient(135deg,#111,#333)',        shadow: 'rgba(0,0,0,0.3)',        color: 'white' },
    { name: 'VS Code',    icon: Monitor,   bg: 'linear-gradient(135deg,#007acc,#0099ff)', shadow: 'rgba(0,122,204,0.35)',   color: 'white' },
    { name: 'HTML5',      icon: Globe,     bg: 'linear-gradient(135deg,#e34f26,#fd7e3b)', shadow: 'rgba(227,79,38,0.35)',   color: 'white' },
    { name: 'CSS3',       icon: Palette,   bg: 'linear-gradient(135deg,#1572b6,#42a5f5)', shadow: 'rgba(21,114,182,0.35)',  color: 'white' },
    { name: 'Bootstrap',  icon: Palette,   bg: 'linear-gradient(135deg,#7952b3,#9c7ed1)', shadow: 'rgba(121,82,179,0.35)',  color: 'white' },
    { name: 'MySQL',      icon: Database,  bg: 'linear-gradient(135deg,#4479a1,#6bb6ff)', shadow: 'rgba(68,121,161,0.35)',  color: 'white' },
  ];

  const testimonials = [
    { name: 'Rajesh P.',  role: 'Backend Developer @ TCS',      text: "Vista Tech's hands-on approach transformed my coding skills. The pay-after-placement model removed all financial stress. Got placed within 2 weeks of completing the program!" },
    { name: 'Priya M.',   role: 'Full Stack Dev @ Infosys',      text: 'The mentorship and real projects at Vista Tech gave me confidence. I built 8+ projects and landed my dream job. Best decision of my career!' },
    { name: 'Arjun K.',   role: 'Frontend Developer @ Wipro',    text: 'From zero coding knowledge to getting placed at Wipro — Vista Tech made it possible. The 90-day program structure is perfect for beginners.' },
    { name: 'Karthik S.', role: 'Software Engineer @ Accenture', text: "Vista Tech's industry connections are amazing. Got multiple job offers after completing the program. The pay-after-placement model is a game-changer!" },
    { name: 'Divya L.',   role: 'Junior Developer @ Cognizant',  text: 'The step-by-step curriculum and mentor support helped me transition from commerce background to tech. Highly recommend Vista Tech!' },
  ];

  const timeSlots: TimeSlot[] = [
    { id: 'morning',   time: '10 AM – 1 PM',  datetime: '10:00 AM - 1:00 PM',  date: 'Morning Session',   available: true  },
    { id: 'afternoon', time: '1 PM – 4 PM',   datetime: '1:00 PM - 4:00 PM',   date: 'Afternoon Session', available: true  },
    { id: 'evening',   time: '4 PM – 7 PM',   datetime: '4:00 PM - 7:00 PM',   date: 'Evening Session',   available: true  },
  ];

  const showCourseSelect =
    formData.completedCourse === 'no' ||
    (formData.completedCourse === 'yes' && formData.previousCourseName?.trim() && formData.previousCourseDuration?.trim());

  const showTimeSlots =
    !!formData.selectedCourse?.trim() && showCourseSelect;

  const stagger: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.1 } },
  };
  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <PageWrapper>
      {/* ── HERO ── */}
      <HeroWrap>
        <HeroBg />
        <Orb $size={400} $top="-10%" $left="-8%"  $delay={0} $color="rgba(78,58,105,0.8)"  />
        <Orb $size={300} $top="60%"  $left="75%"  $delay={2} $color="rgba(255,101,53,0.6)" />
        <Orb $size={200} $top="20%"  $left="80%"  $delay={4} $color="rgba(240,180,41,0.5)" />
        <Orb $size={150} $top="75%"  $left="10%"  $delay={1} $color="rgba(78,58,105,0.6)"  />

        <HeroContent>
          <HeroBadge
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles /> Vista Tech Solutions — 50% Pay After Placement
          </HeroBadge>

          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            Train. Build. <span className="g">Get Hired.</span><br />Then Pay Us.
          </HeroTitle>

          <HeroSub
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Learn from industry experts, build real projects, get guaranteed job placement —
            and only pay <strong style={{ color: C.accent }}>after you land your first job</strong>.
          </HeroSub>

          <HeroStats
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            {[
              { num: '90',  lbl: 'Day Program'    },
              { num: '98%', lbl: 'Placement Rate' },
            ].map((s, i) => (
              <StatPill key={i} whileHover={{ scale: 1.05 }}>
                <span className="num">{s.num}</span>
                <span className="lbl">{s.lbl}</span>
              </StatPill>
            ))}
          </HeroStats>
        </HeroContent>
      </HeroWrap>

      {/* ── CONTENT ── */}
      <ContentWrap>

        {/* FORM */}
        <Section $alt>
          <Container>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Tag><Rocket />Start Your Journey</Tag>
              <Heading $center>Book Your <span className="a">Free</span> Consultation</Heading>
              <Sub $center>Fill in your details and we'll reach out at your chosen time slot.</Sub>
            </div>

            <FormCard
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <FormHead>
                <h3>🚀 Start Your <span>Tech Career</span> Journey</h3>
                <p>Complete the form below — it takes less than 3 minutes</p>
              </FormHead>

              <FormBody>
                <OfferBanner>
                  <Trophy /> <span className="hl">Limited Seats</span> — Only a few spots left this batch!
                </OfferBanner>

                <MiniStatsRow>
                  <MiniStat>
                    <div className="v"><Clock /> 90</div>
                    <div className="k">Days to Job-Ready</div>
                  </MiniStat>
                  <MiniStat>
                    <div className="v"><Target /> 100%</div>
                    <div className="k">Placement Guarantee</div>
                  </MiniStat>
                </MiniStatsRow>


                <form onSubmit={handleSubmit} ref={formRef} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

                  {/* Personal Info */}
                  <FSTitle>
                    <span className="icon-wrap"><UserCheck /></span>
                    Personal Information
                  </FSTitle>

                  <FieldGrid $cols={2}>
                    <Field>
                      <FLabel><Users />Full Name <span className="req">*</span></FLabel>
                      <SInput type="text" name="name" value={formData.name} onChange={handleInput} placeholder="Enter your full name" required />
                    </Field>
                    <Field>
                      <FLabel><Phone />WhatsApp Number <span className="req">*</span></FLabel>
                      <SInput type="tel" name="phone" value={formData.phone} onChange={handleInput} placeholder="10-digit mobile number" pattern="[0-9]{10}" maxLength={10} required />
                    </Field>
                  </FieldGrid>

                  <Field>
                    <FLabel><Mail />Email Address <span className="req">*</span></FLabel>
                    <SInput type="email" name="email" value={formData.email} onChange={handleInput} placeholder="your.email@example.com" required />
                  </Field>

                  {/* Education */}
                  <FSTitle>
                    <span className="icon-wrap"><GraduationCap /></span>
                    Education Details
                  </FSTitle>

                  <FieldGrid $cols={2}>
                    <Field>
                      <FLabel><Calendar />Year of Graduation</FLabel>
                      <SSelect name="yearOfPassout" value={formData.yearOfPassout} onChange={handleInput}>
                        <option value="">Select Year</option>
                        {['2026','2025','2024','2023','2022','2021','2020','other'].map(y => <option key={y} value={y}>{y === 'other' ? 'Other' : y}</option>)}
                      </SSelect>
                    </Field>
                    <Field>
                      <FLabel><Award />Degree / Qualification</FLabel>
                      <SSelect name="degree" value={formData.degree} onChange={handleInput}>
                        <option value="">Select Degree</option>
                        {[['btech','B.Tech'],['bca','BCA'],['mca','MCA'],['bsc','B.Sc'],['mtech','M.Tech'],['diploma','Diploma'],['other','Other']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                      </SSelect>
                    </Field>
                  </FieldGrid>

                  <FieldGrid $cols={2}>
                    <Field>
                      <FLabel><BookOpen />College / University <span className="req">*</span></FLabel>
                      <SInput type="text" name="collegeName" value={formData.collegeName} onChange={handleInput} placeholder="Enter your college name" required />
                    </Field>
                    <Field>
                      <FLabel><MapPin />Current Location <span className="req">*</span></FLabel>
                      <SInput type="text" name="location" value={formData.location} onChange={handleInput} placeholder="City, State" required />
                    </Field>
                  </FieldGrid>

                  {/* Course Info */}
                  <FSTitle>
                    <span className="icon-wrap"><Code2 /></span>
                    Course Information
                  </FSTitle>

                  <Field>
                    <FLabel>Completed any programming course before? <span className="req">*</span></FLabel>
                    <RadioCards>
                      {[['yes','Yes ✅'],['no','No 🚀']].map(([v,l]) => (
                        <RCard key={v} $on={formData.completedCourse === v} onClick={() => handleRadio('completedCourse', v)}>
                          <span className="dot" />
                          {l}
                        </RCard>
                      ))}
                    </RadioCards>
                  </Field>

                  <AnimatePresence>
                    {formData.completedCourse === 'yes' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}
                      >
                        <FieldGrid $cols={2}>
                          <Field>
                            <FLabel><BookOpenCheck />Course Completed <span className="req">*</span></FLabel>
                            <SInput type="text" name="previousCourseName" value={formData.previousCourseName} onChange={handleInput} placeholder="e.g., Full Stack Development" required />
                          </Field>
                          <Field>
                            <FLabel><Clock />Course Duration <span className="req">*</span></FLabel>
                            <SSelect name="previousCourseDuration" value={formData.previousCourseDuration} onChange={handleInput} required>
                              <option value="">Select Duration</option>
                              {[['less-than-1-month','< 1 month'],['1-3-months','1–3 months'],['3-6-months','3–6 months'],['6-12-months','6–12 months'],['more-than-1-year','> 1 year']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                            </SSelect>
                          </Field>
                        </FieldGrid>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {showCourseSelect && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                        <Field>
                          <FLabel><Briefcase />Which course interests you? <span className="req">*</span></FLabel>
                          <SSelect name="selectedCourse" value={formData.selectedCourse} onChange={handleInput} required>
                            <option value="">Select Course</option>
                            {[['business-analytics','Business Analytics'],['full-stack-java','Full Stack Java Development'],['data-science','Data Science'],['full-stack-python','Full Stack Python Development'],['data-analyst','Data Analyst']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                          </SSelect>
                        </Field>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Time Slots */}
                  <AnimatePresence>
                    {showTimeSlots && (
                      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                        <FSTitle>
                          <span className="icon-wrap"><MessageCircle /></span>
                          Book Technical Consultation
                        </FSTitle>

                        <FLabel style={{ marginBottom: '0.75rem' }}>
                          Preferred time slot <span className="req">*</span>
                        </FLabel>

                        <TimeGrid>
                          {timeSlots.map(slot => (
                            <TCard
                              key={slot.id}
                              $on={formData.consultationSlot === slot.datetime}
                              $off={!slot.available}
                              onClick={() => slot.available && handleRadio('consultationSlot', slot.datetime)}
                            >
                              <div className="si">
                                {slot.id === 'morning'   && <Zap />}
                                {slot.id === 'afternoon' && <Target />}
                                {slot.id === 'evening'   && <Star />}
                              </div>
                              <div className="st">{slot.time}</div>
                              <div className="sl">{slot.date}</div>
                              <div className="ss">{slot.available ? '● Available' : '✕ Full'}</div>
                            </TCard>
                          ))}
                        </TimeGrid>

                        <NoteBox>
                          <div className="nt"><CheckCircle />Our counselor will:</div>
                          <ul>
                            {['Discuss your career goals & course details','Answer all program questions','Explain the pay-after-placement model','Help choose the right learning path'].map(p => (
                              <li key={p}><CheckCircle />{p}</li>
                            ))}
                          </ul>
                        </NoteBox>

                        <NoteBox $variant="warn">
                          <div className="nt"><TrendingUp />Time slot selection is required to proceed.</div>
                        </NoteBox>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {validationError && (
                      <VError initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                        <Shield /> {validationError}
                      </VError>
                    )}
                  </AnimatePresence>

                  <SubmitBtn
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Rocket />
                    {isSubmitting ? 'Booking Your Slot…' : 'Book Free Consultation & Start Journey!'}
                  </SubmitBtn>

                  <TrustRow>
                    {['100% Free Consultation','No Hidden Charges','Industry Expert Guidance'].map(t => (
                      <span key={t} className="t"><CheckCircle />{t}</span>
                    ))}
                  </TrustRow>

                  <AnimatePresence>
                    {submitStatus === 'success' && (
                      <SuccessBox initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
                        <div className="ic"><CheckCircle /></div>
                        <h4>Registration Successful!</h4>
                        <p>Our technical counselor will call you at your selected time slot. Check your email for confirmation.</p>
                      </SuccessBox>
                    )}
                    {submitStatus === 'error' && (
                      <ErrorBox initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        Something went wrong. Please try again or call us directly.
                        {errorMessage && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>{errorMessage}</div>}
                      </ErrorBox>
                    )}
                  </AnimatePresence>

                </form>
              </FormBody>
            </FormCard>
          </Container>
        </Section>

        {/* WHY VISTA TECH */}
        <Section>
          <Container>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}>
              <motion.div variants={fadeUp} style={{ textAlign: 'center' }}>
                <Tag><Shield />Why Us</Tag>
                <Bar style={{ margin: '0 auto 1rem' }} />
                <Heading $center>Why Vista Tech <span className="a">50% Pay After Placement?</span></Heading>
                <Sub $center>We believe in your potential. We only get paid when you land your dream job.</Sub>
              </motion.div>

              <FGrid>
                {[
                  { icon: BookOpen,   title: 'Industry-Ready Curriculum',   text: 'Designed by experts and updated to match current market demands. Learn exactly what companies are hiring for.' },
                  { icon: Users,      title: 'Live Mentorship',              text: 'Get guidance from developers at top tech companies. Code reviews, mock interviews, and career support included.' },
                  { icon: Briefcase,  title: '10+ Real Projects',            text: 'Build projects you can showcase to employers — from web apps to full-stack systems with auth and databases.' },
                  { icon: Trophy,     title: '100% Placement Support',       text: 'Resume building, interview prep, and job referrals. Our placement team works until you get hired.' },
                ].map((f, i) => (
                  <FCard key={i} variants={fadeUp} whileHover={{ y: -7 }}>
                    <FIcon className="fi"><f.icon /></FIcon>
                    <h3 style={{ color: C.primary, fontWeight: 800, fontSize: '1.05rem', marginBottom: '0.6rem' }}>{f.title}</h3>
                    <p style={{ color: '#777', fontSize: '0.875rem', lineHeight: 1.7, margin: 0 }}>{f.text}</p>
                  </FCard>
                ))}
              </FGrid>
            </motion.div>
          </Container>
        </Section>


        {/* TESTIMONIALS */}
        <Section $alt>
          <Container>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Tag><Star />Success Stories</Tag>
              <Heading $center>Our Students <span className="a">Love Us</span></Heading>
              <Sub $center>Hear from freshers who went from zero to placed.</Sub>
            </div>
          </Container>

          <TestiWrap>
            <TestiTrack>
              {[...testimonials, ...testimonials].map((t, i) => (
                <TestiCard key={i}>
                  <TAvatar>{t.name.split(' ').map(n => n[0]).join('')}</TAvatar>
                  <div style={{ fontWeight: 800, color: C.primary, fontSize: '0.9rem', marginBottom: '0.2rem' }}>{t.name}</div>
                  <div style={{ fontSize: '0.75rem', color: C.accent, fontWeight: 700, marginBottom: '0.875rem' }}>{t.role}</div>
                  <p style={{ fontSize: '0.84rem', color: '#666', lineHeight: 1.7, fontStyle: 'italic', marginBottom: '0.875rem' }}>{t.text}</p>
                  <div style={{ display: 'flex', gap: '0.2rem' }}>
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill={C.accent} style={{ color: C.accent }} />)}
                  </div>
                </TestiCard>
              ))}
            </TestiTrack>
          </TestiWrap>
        </Section>

        {/* JOURNEY */}
        <Section>
          <Container>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger}>
              <motion.div variants={fadeUp} style={{ textAlign: 'center' }}>
                <Tag><Rocket />Roadmap</Tag>
                <Heading $center>Your <span className="a">90-Day</span> Success Journey</Heading>
                <Sub $center>From complete beginner to job-ready developer in 3 months.</Sub>
              </motion.div>

              <JGrid>
                {[
                  { icon: BookOpen,    title: 'Learn & Build',        desc: '60 days of intensive learning with live projects, daily coding practice, and expert mentorship.',         badge: 'Days 1–60',    },
                  { icon: Briefcase,   title: 'Real Work Experience', desc: '30-day paid internship working on live client projects alongside our core development team.',             badge: 'Days 61–90',   },
                  { icon: Trophy,      title: 'Get Placed',           desc: 'Interview preparation, resume building, and job placement with our growing network of hiring partners.',  badge: 'Guaranteed',   },
                  { icon: TrendingUp,  title: 'Grow Your Career',     desc: 'Lifetime alumni network access, referrals, and continuous career growth support — forever.',              badge: 'Forever',      },
                ].map((s, i) => (
                  <JStep key={i} variants={fadeUp}>
                    <JBubble>
                      <s.icon />
                      <JNum>{i + 1}</JNum>
                    </JBubble>
                    <h4 style={{ fontWeight: 800, color: C.primary, fontSize: '0.98rem', marginBottom: '0.5rem' }}>{s.title}</h4>
                    <p style={{ fontSize: '0.82rem', color: '#888', lineHeight: 1.65, marginBottom: '0.75rem' }}>{s.desc}</p>
                    <StepBadge>{s.badge}</StepBadge>
                  </JStep>
                ))}
              </JGrid>
            </motion.div>
          </Container>
        </Section>

      </ContentWrap>
    </PageWrapper>
  );
};

const StepBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, ${C.accent}20, ${C.orange}15);
  border: 1px solid ${C.accent}35;
  color: ${C.primary};
  padding: 0.25rem 0.875rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export default PayAfterForm;
